import { fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { getDeviceIdFromSession, getSelectedAccountInfo } from '@ibk/shared-auth';
import { AppDomain } from '@ibk/shared-types';
import { config } from '../config';
import { ENABLED_FEATURE_HEADER, getEnabledFeature } from '../developerSettings';
import { selectAppDomain } from '../store/selectors/applicationSelector';
import { selectSelectedAccountId } from '../store/selectors/userSelectors';
import { attemptTokenRefresh, createApiError, createAuthHeaders, extractUrl, getTimeout, processTokenFromResponse, shouldAttemptRefresh, } from './authUtils';
import { XApplicationNameEnum } from './types';
const DEFAULT_TIMEOUT = 30000;
const isStringArgs = (args) => typeof args === 'string';
const getAccountId = (state, args) => {
    const forceSelectedAccountInfoAccountId = isStringArgs(args)
        ? false
        : args?.forceSelectedAccountInfoAccountId;
    return forceSelectedAccountInfoAccountId ? getSelectedAccountInfo()?.accountId : selectSelectedAccountId(state);
};
const getTransactionIdempotencyId = (args) => {
    return isStringArgs(args) ? undefined : args?.transactionIdempotencyId;
};
const buildIbkFetchBaseQuery = (baseUrl, timeout) => {
    return fetchBaseQuery({
        baseUrl,
        timeout,
        prepareHeaders: (headers, { getState, arg }) => {
            const body = typeof arg === 'object' && arg !== null && 'body' in arg ? arg.body : undefined;
            if (!(body instanceof FormData)) {
                headers.set('Content-Type', 'application/json');
            }
            const state = getState();
            const authHeaders = createAuthHeaders(state);
            if (authHeaders.Authorization) {
                headers.set('Authorization', authHeaders.Authorization);
            }
            const accountId = getAccountId(state, arg);
            if (accountId) {
                headers.set('account-id', accountId);
            }
            const deviceId = getDeviceIdFromSession();
            if (deviceId) {
                headers.set('device-id', deviceId);
            }
            const appDomain = selectAppDomain(state);
            if (appDomain) {
                headers.set('X-Application-Name', XApplicationNameEnum[appDomain]);
            }
            const transactionIdempotencyId = getTransactionIdempotencyId(arg);
            if (transactionIdempotencyId) {
                headers.set('transaction-idempotency-id', transactionIdempotencyId);
            }
            const enabledFeature = getEnabledFeature();
            if (enabledFeature) {
                headers.set(ENABLED_FEATURE_HEADER, enabledFeature);
            }
            return headers;
        },
    });
};
export const createIbkBaseQuery = (baseUrl, timeout = DEFAULT_TIMEOUT) => {
    return async (args, api, extraOptions) => {
        const url = extractUrl(args);
        const requestTimeout = getTimeout(args, timeout);
        const baseQuery = buildIbkFetchBaseQuery(baseUrl, requestTimeout);
        const queryArgs = typeof args === 'string' ? url : { ...args, url };
        const result = await baseQuery(queryArgs, api, extraOptions);
        processTokenFromResponse(result.data, api);
        if (result.error) {
            const fetchError = result.error;
            if (shouldAttemptRefresh(fetchError)) {
                const refreshSuccess = await attemptTokenRefresh(api);
                if (refreshSuccess) {
                    const refreshedBaseQuery = buildIbkFetchBaseQuery(baseUrl, requestTimeout);
                    return refreshedBaseQuery(queryArgs, api, extraOptions);
                }
            }
            if ('status' in fetchError) {
                return { error: createApiError(fetchError) };
            }
        }
        return result;
    };
};
export const resolveIbkBffUrlByAppDomain = (state) => selectAppDomain(state) === AppDomain.ESCRITORIO ? config.apiAccountantIbkUrl : config.apiClientIbkUrl;
export const createIbkDomainBaseQuery = (timeout = DEFAULT_TIMEOUT) => {
    return (args, api, extraOptions) => {
        const baseUrl = resolveIbkBffUrlByAppDomain(api.getState());
        return createIbkBaseQuery(baseUrl, timeout)(args, api, extraOptions);
    };
};
